/* eslint-disable @typescript-eslint/ban-ts-comment */
// @ts-nocheck - Material Tailwind component prop warnings
import React, { FC, useState } from 'react';
import {
  LuGlobe,
  LuLock,
  LuMessageSquare,
  LuDatabase,
  LuUsers,
  LuPuzzle,
  LuChevronDown,
  LuCheckCircle,
  LuBell,
} from 'react-icons/lu';
import AppToggle from '@/components/atoms/AppToggle';
import ConfigAccordion from '@/components/elements/ConfigAccordion';
import {
  PROVIDER_CONFIG_PROPS,
  AUTH_TYPE_OPTIONS,
} from '@/constant/provider/provider';
import ConfigInput from '@/components/elements/ConfigInput';
import ConfigDropdown from '@/components/elements/ConfigDropdown';
import ConfigToggle from '@/components/elements/ConfigToggle';
import {
  PROVIDER_CONFIG_FIELD_ICONS,
  PROVIDER_SERVICE_ICONS,
} from '@/constant/provider/providerIcons';
import { APP_BG } from '@/constant/theme/colors.constant';
import { PROVIDER_TEST_IDS } from '@/constant/testIds/provider.testIds';
import { AlertsConfig, SubServiceConfig } from '../config.types';
import { CONFIG_COLORS } from '../configColors.constants';
import { isValidUrl } from '../urlValidator';
import { tid } from '@/utils/testId';

interface AlertsConfigSectionProps {
  config: AlertsConfig;
  isOpen: boolean;
  onToggle: () => void;
  onChange: (config: AlertsConfig) => void;
  hasError?: boolean;
}

const AlertsConfigSection: FC<AlertsConfigSectionProps> = ({
  config,
  isOpen,
  onToggle,
  onChange,
  hasError,
}) => {
  const [openSubSection, setOpenSubSection] = useState<string | null>(null);
  const colors = CONFIG_COLORS.ALERTS;

  const handleSubSectionToggle = (section: string) => {
    setOpenSubSection(openSubSection === section ? null : section);
  };

  const updateSubService = (
    key: keyof AlertsConfig,
    subService: SubServiceConfig,
  ) => {
    onChange({ ...config, [key]: subService });
  };

  const getIcon = (key: string) => {
    switch (key) {
      case 'MMS':
        return <PROVIDER_SERVICE_ICONS.MMS className="w-4 h-4 text-white" />;
      case 'VMS':
        return <PROVIDER_SERVICE_ICONS.VMS className="w-4 h-4 text-white" />;
      case 'CLIENTS':
        return (
          <PROVIDER_SERVICE_ICONS.CLIENTS className="w-4 h-4 text-white" />
        );
      case 'GATEWAY':
        return <LuPuzzle className="w-4 h-4 text-white" />;
      default:
        return <div className="w-2 h-2 rounded-full bg-white" />;
    }
  };

  const renderSubService = (key: keyof AlertsConfig, title: string) => {
    const subService = config[key] as SubServiceConfig;
    const isOpen = openSubSection === key;

    const hostError =
      subService.ENABLED &&
        subService.AUTH.AUTH_TYPE &&
        subService.AUTH.AUTH_TYPE !== 'none'
        ? !subService.AUTH.HOST
          ? 'Host URL is required'
          : !isValidUrl(subService.AUTH.HOST)
            ? 'Must be a valid URL (e.g. https://api.example.com)'
            : undefined
        : undefined;

    const handleSubEnabledChange = (checked: boolean) => {
      if (!checked) {
        updateSubService(key, { ...subService, ENABLED: false, GET_STATE: false });
      } else {
        const newAuthType = subService.AUTH.AUTH_TYPE || 'none';
        const newHost =
          subService.AUTH.HOST || (newAuthType === 'none' ? 'https://' : '');
        updateSubService(key, {
          ...subService,
          ENABLED: true,
          AUTH: { HOST: newHost, AUTH_TYPE: newAuthType },
        });
      }
    };

    const handleAuthTypeChange = (value: string) => {
      const newHost =
        value === 'none' && !subService.AUTH.HOST
          ? 'https://'
          : subService.AUTH.HOST;
      updateSubService(key, {
        ...subService,
        AUTH: { HOST: newHost, AUTH_TYPE: value },
      });
    };

    return (
      <div className="border border-gray-300 dark:border-gray-600 rounded-lg shadow-sm">
        <div
          role="button"
          tabIndex={0}
          onClick={() => handleSubSectionToggle(key)}
          onKeyDown={(e) => e.key === 'Enter' && handleSubSectionToggle(key)}
          data-testid={tid(PROVIDER_TEST_IDS.CRUD_ALERTS_SUB_HEADER(key as string))}
          className={`w-full ${APP_BG.base} hover:bg-gray-50 dark:hover:bg-gray-700 px-4 py-3 flex items-center justify-between cursor-pointer transition-all select-none ${isOpen ? 'rounded-t-lg' : 'rounded-lg'}`}
        >
          <div className="flex items-center gap-3">
            <div
              className={`w-8 h-8 rounded-lg ${colors.subSectionIconBg} flex items-center justify-center shadow-sm flex-shrink-0`}
            >
              {getIcon(key as string)}
            </div>
            <div>
              <p className="text-sm font-bold text-gray-900 dark:text-white leading-tight">
                {title}
              </p>
              <p
                className={`text-xs font-medium leading-tight mt-0.5 ${subService.ENABLED ? 'text-green-600 dark:text-green-400' : 'text-gray-400 dark:text-gray-500'}`}
              >
                {subService.ENABLED ? 'Enabled' : 'Disabled'}
              </p>
            </div>
          </div>
          <div className="flex items-center gap-2 flex-shrink-0">
            <div
              onClick={(e) => e.stopPropagation()}
              className="flex items-center"
            >
              <AppToggle
                checked={subService.ENABLED}
                onChange={handleSubEnabledChange}
                color={colors.switchColor}
                size="sm"
                disabled={!config.ENABLED}
                data-testid={tid(PROVIDER_TEST_IDS.CRUD_ALERTS_SUB_TOGGLE(
                  key as string,
                ))}
              />
            </div>
            <LuChevronDown
              className={`w-4 h-4 flex-shrink-0 transition-transform duration-300 text-gray-500 dark:text-gray-400 ${isOpen ? 'rotate-180' : ''}`}
            />
          </div>
        </div>
        {isOpen && (
          <div
            className={`p-4 space-y-4 ${APP_BG.canvas} border-t border-gray-300 dark:border-gray-600 rounded-b-lg`}
          >
            <ConfigInput
              label={PROVIDER_CONFIG_PROPS.FIELDS.HOST_URL}
              value={subService.AUTH.HOST}
              onChange={(e) =>
                updateSubService(key, {
                  ...subService,
                  AUTH: { ...subService.AUTH, HOST: e.target.value },
                })
              }
              placeholder="https://api.example.com"
              focusRingColor={colors.focusRingColor}
              iconColor={colors.iconColor}
              required={
                subService.ENABLED &&
                !!subService.AUTH.AUTH_TYPE &&
                subService.AUTH.AUTH_TYPE !== 'none'
              }
              error={hostError}
              icon={PROVIDER_CONFIG_FIELD_ICONS.HOST}
              dataTestId={tid(PROVIDER_TEST_IDS.CRUD_ALERTS_SUB_HOST(key as string))}
              disabled={!config.ENABLED || !subService.ENABLED}
            />
            <div className="grid grid-cols-1 gap-4 items-start md:grid-cols-2">
              <ConfigDropdown
                label={PROVIDER_CONFIG_PROPS.FIELDS.AUTH_TYPE}
                value={subService.AUTH.AUTH_TYPE}
                options={AUTH_TYPE_OPTIONS}
                onChange={handleAuthTypeChange}
                placeholder={PROVIDER_CONFIG_PROPS.FIELDS.AUTH_TYPE_PLACEHOLDER}
                activeBorderClass={colors.toggleCheckedBorder}
                iconColor={colors.iconColor}
                itemSelectedBg={colors.itemSelectedBg}
                accentColor={colors.iconColor}
                focusBorderColor={colors.accentFocusBorder}
                selectedGradient={colors.dropdownSelectedGradient}
                accentBg={colors.accentBg}
                accentDivider={colors.accentDivider}
                icon={PROVIDER_CONFIG_FIELD_ICONS.AUTH_TYPE}
                dataTestId={tid(PROVIDER_TEST_IDS.CRUD_ALERTS_SUB_AUTH_TYPE(
                  key as string,
                ))}
                disabled={!config.ENABLED || !subService.ENABLED}
              />
              {/* GET_STATE Toggle */}
              <ConfigToggle
                label={PROVIDER_CONFIG_PROPS.FIELDS.GET_STATE}
                checked={subService.GET_STATE}
                onChange={(checked) =>
                  updateSubService(key, { ...subService, GET_STATE: checked })
                }
                description={
                  subService.GET_STATE
                    ? PROVIDER_CONFIG_PROPS.FIELDS.ENABLED
                    : PROVIDER_CONFIG_PROPS.FIELDS.DISABLED
                }
                toggleCheckedBg={colors.toggleCheckedBg}
                toggleCheckedBorder={colors.toggleCheckedBorder}
                toggleCheckedText={colors.toggleCheckedText}
                toggleCheckedIconBg={colors.toggleCheckedIconBg}
                customColor={colors.switchColor}
                icon={
                  <PROVIDER_CONFIG_FIELD_ICONS.GET_STATE className="w-4 h-4" />
                }
                disabled={!config.ENABLED || !subService.ENABLED}
                testId={tid(PROVIDER_TEST_IDS.CRUD_ALERTS_SUB_GET_STATE(
                  key as string,
                ))}
              />
            </div>
          </div>
        )}
      </div>
    );
  };

  return (
    <ConfigAccordion
      index={4}
      isOpen={isOpen}
      onToggle={onToggle}
      title={PROVIDER_CONFIG_PROPS.SECTIONS.ALERTS_TITLE}
      enabled={config.ENABLED}
      onEnabledChange={(enabled) => {
        if (!enabled) {
          onChange({
            ...config,
            ENABLED: false,
            MMS: { ...config.MMS, ENABLED: false, GET_STATE: false },
            VMS: { ...config.VMS, ENABLED: false, GET_STATE: false },
            CLIENTS: { ...config.CLIENTS, ENABLED: false, GET_STATE: false },
            GATEWAY: { ...config.GATEWAY, ENABLED: false, GET_STATE: false },
          });
        } else {
          onChange({ ...config, ENABLED: true });
        }
      }}
      iconColor={colors.accordionIcon}
      titleColor={colors.accordionTitle}
      customColor={colors.switchColor}
      hasError={hasError}
      customIcon={<PROVIDER_SERVICE_ICONS.ALERTS className="w-5 h-5" />}
      testId={tid(PROVIDER_TEST_IDS.CRUD_ALERTS_ACCORDION)}
    >
      <div className="space-y-4">
        <div className="space-y-3">
          {renderSubService('MMS', 'MMS Settings')}
          {renderSubService('VMS', 'VMS Settings')}
          {renderSubService('CLIENTS', 'CLIENTS Settings')}
          {renderSubService('GATEWAY', 'GATEWAY Settings')}
        </div>
      </div>
    </ConfigAccordion>
  );
};

export default AlertsConfigSection;
